import { App as AntApp, Typography } from "antd";
import type { NotificationProvider } from "@refinedev/core";
import { t } from "i18next";
import type { ReactNode } from "react";

// Refine notification keys used for its mid-flow "redirect to 2FA" case,
// see MFA_REDIRECT_ERROR in createAuthProvider.
const MFA_REDIRECT_NOTIFICATION_KEYS = new Set([
  "login-error",
  "update-password-error",
]);

/**
 * True when a notification originates from a network-level failure
 * (no HTTP response received). We swallow these globally so a single
 * outage doesn't produce a stack of "Network Error" toasts — one per
 * in-flight query. Page-level UX (e.g. the login alert) handles user
 * messaging for these cases.
 */
const isNetworkErrorNotification = (args: {
  message?: ReactNode;
  description?: ReactNode;
}): boolean => {
  const messageStr = typeof args.message === "string" ? args.message : "";
  const descriptionStr =
    typeof args.description === "string" ? args.description : "";
  // adaptAxiosError sets statusCode to 0 when the request never gets a
  // response (network/CORS/timeout). Refine renders that as "(status code: 0)".
  if (/\(status code:\s*0\)/i.test(messageStr)) return true;
  if (messageStr === "Network Error" || descriptionStr === "Network Error") {
    return true;
  }
  return false;
};

/**
 * Custom notification provider that uses AntApp.useApp() for context-aware notifications.
 * This allows notifications to consume Ant Design's dynamic theme context.
 */
export const useContextNotificationProvider = (): NotificationProvider => {
  const { notification } = AntApp.useApp();

  const getFriendlyErrorMessage = (error: {
    message: string;
    description?: string | { message?: string };
  }) => {
    const ErrorMessages: Record<number, { message: string }> = {
      404: {
        message: t("src.App.resourceNotFound"),
      },
    };
    const parsedStatusCode = Number(error?.message.match(/\d{3}/)?.[0]);
    const friendly = ErrorMessages[parsedStatusCode];

    let description = error?.description;
    if (typeof description !== "string" && description?.message) {
      description = description.message;
    } else if (typeof description !== "string") {
      description = JSON.stringify(description);
    }
    const formattedOriginal = (
      <Typography.Text type="secondary">
        {description} • {error.message}
      </Typography.Text>
    );
    return {
      ...error,
      message: (
        <>
          {friendly && (
            <>
              {friendly?.message}
              <br />
            </>
          )}
          {formattedOriginal}
        </>
      ),
      description: t("src.App.somethingWentWrong"),
    };
  };

  return {
    open: (params) => {
      const args = { ...params } as typeof params & { skipFriendly?: boolean };
      if (args.message && typeof args.message === "string") {
        args.message = args.message.replaceAll("&#x2F;", "/");
      }
      // Do not show session expired errors
      if (
        (args?.description as unknown as { code: string })?.code ===
        "SESSION_EXPIRED"
      ) {
        return;
      }
      // Suppress network-failure toasts globally — they spam one per
      // failed query during VPN/firewall/offline outages. Page-level
      // UX surfaces these conditions instead.
      if (args.type === "error" && isNetworkErrorNotification(args)) {
        return;
      }
      // Suppress refine's generic error toast when it's just a mid-flow
      // redirect to the 2FA step, not a real failure. createAuthProvider
      // tags that case via error.name = "MFA_REQUIRED" on both the login
      // and update-password auth actions.
      if (
        MFA_REDIRECT_NOTIFICATION_KEYS.has(args.key as string) &&
        args.message === "MFA_REQUIRED"
      ) {
        return;
      }
      if (args.type === "error" && !args.skipFriendly) {
        try {
          const friendly = getFriendlyErrorMessage(
            args as {
              message: string;
              description?: string | { message?: string };
            }
          );
          notification.open({
            type: args.type,
            message: friendly.message,
            description: friendly.description,
            placement: "bottomLeft",
          });
        } catch (error) {
          console.error(error);
          notification.error({
            message: t("src.App.error"),
            description: t("src.App.somethingWentWrong"),
            placement: "bottomLeft",
          });
        }
      } else {
        const notificationType = args.type === "progress" ? "info" : args.type;
        notification.open({
          type: notificationType,
          message: args.message,
          description: args.description,
          placement: "bottomLeft",
        });
      }
      console.debug("notification", args);
    },
    close: (key) => {
      notification.destroy(key);
    },
  };
};
